import { api } from "./client";

export type EnrollmentCode = {
  id: string;
  code: string;
  expires_at: string;
  created_at: string;
  ttl_seconds: number;
};

export type AgentRow = {
  id: string;
  hostname: string;
  label: string;
  status: "online" | "offline" | "provisioning" | "needs_attention" | "uninstalled" | "blocked";
  last_seen_at: string | null;
  os: string;
  os_version: string;
  agent_version: string;
  tailscale_ip: string | null;
  client_cert_expires_at: string | null;
  last_heartbeat_error: string;
  last_heartbeat_error_at: string | null;
  created_at: string;
  update_available: boolean;
  // Whether this PC can use the office's shared printers. "granted" by
  // default (enrollment is the approval); "none" once the owner revokes.
  resource_access: "granted" | "none";
};

export type AgentListResponse = { results: AgentRow[] };

export async function createEnrollmentCode() {
  const { data } = await api.post<EnrollmentCode>("/enrollment-codes", {});
  return data;
}

export async function listAgents() {
  const { data } = await api.get<AgentListResponse>("/agents/");
  return data;
}

export async function renameAgent(id: string, label: string) {
  const { data } = await api.patch<{ ok: boolean; id: string; label: string; hostname: string }>(
    `/agents/${id}/rename`,
    { label },
  );
  return data;
}

export async function removeAgent(id: string) {
  const { data } = await api.post<{ ok: boolean }>(`/agents/${id}/remove`);
  return data;
}

export async function blockAgent(id: string) {
  const { data } = await api.post<{ ok: boolean }>(`/agents/${id}/block`);
  return data;
}

export async function resendAgentSetup(id: string) {
  const { data } = await api.post<{ code: string; expires_at: string; ttl_seconds: number }>(
    `/agents/${id}/resend-setup`,
  );
  return data;
}

export async function unblockAgent(id: string) {
  const { data } = await api.post<{ ok: boolean }>(`/agents/${id}/unblock`);
  return data;
}

// Self-serve "turn it off and on again" — bounces the whole KraetdNet
// Windows service on that PC. General escape hatch for a stuck agent (a
// printer that vanished from the local print dialog and never came back,
// a wedged Tailscale connection, etc.) without needing admin access to the
// machine itself. Returns a task ticket the caller polls with
// getAgentRestartStatus; the agent's own status/last_seen_at flipping is
// the real "it's back" signal, not the task status alone.
export type AgentRestartTicket = { task_id: string; status: string; agent_id: string };

export async function restartAgent(id: string) {
  const { data } = await api.post<AgentRestartTicket>(`/agents/${id}/restart`);
  return data;
}

export type AgentRestartStatus = {
  task_id: string;
  status: "pending" | "running" | "succeeded" | "failed" | "expired" | "cancelled";
  error_message: string;
};

export async function getAgentRestartStatus(taskId: string) {
  const { data } = await api.get<AgentRestartStatus>(`/agents/restarts/${taskId}`);
  return data;
}

// Owner cuts off / restores a single PC's access to the office printers.
// Enrolled PCs can print by default; revoking is the per-PC kill-switch
// that doesn't require uninstalling the agent.
export async function revokeAgentAccess(id: string) {
  const { data } = await api.post<{ ok: boolean; resource_access: string }>(
    `/agents/${id}/revoke-access`,
  );
  return data;
}

export async function grantAgentAccess(id: string) {
  const { data } = await api.post<{ ok: boolean; resource_access: string }>(
    `/agents/${id}/grant-access`,
  );
  return data;
}

export type DeleteWarning = {
  shares_hosted: number;
  printers_hosted: number;
  mounted_drives: number;
  installed_printers: number;
  backup_jobs: number;
  backup_runs: number;
};

export type TransferCandidate = {
  id: string;
  label: string;
  hostname: string;
  status: AgentRow["status"];
};

export type DeleteProbeResult = {
  would_delete: DeleteWarning;
  transfer_candidates: TransferCandidate[];
};

/**
 * deleteAgent — two-step delete with optional transfer.
 *
 * First call (no opts) probes for dependents. Returns:
 *   - `null` if the agent had nothing attached (delete already happened, 204)
 *   - a {@link DeleteProbeResult} if there were dependents (409): both the
 *     destruction summary and the list of agents the dashboard can offer
 *     as transfer targets.
 *
 * Second call: pass either `force: true` to delete everything OR
 * `transferTo: <agent_id>` to reassign shared folders / printers /
 * backup jobs to another agent and then delete the original.
 */
export async function deleteAgent(
  id: string,
  opts: { force?: boolean; transferTo?: string } = {},
): Promise<DeleteProbeResult | null> {
  const params = new URLSearchParams();
  if (opts.force) params.set("force", "true");
  if (opts.transferTo) params.set("transfer_to", opts.transferTo);
  const url = params.toString() ? `/agents/${id}?${params.toString()}` : `/agents/${id}`;
  try {
    await api.delete(url);
    return null;
  } catch (e: unknown) {
    const err = e as {
      response?: {
        status?: number;
        data?: {
          would_delete?: DeleteWarning;
          transfer_candidates?: TransferCandidate[];
        };
      };
    };
    if (err.response?.status === 409 && err.response.data?.would_delete) {
      return {
        would_delete: err.response.data.would_delete,
        transfer_candidates: err.response.data.transfer_candidates ?? [],
      };
    }
    throw e;
  }
}

export type SetupSessionInfo = {
  code: string;
  hostname: string;
  status: "pending" | "confirmed" | "expired";
  created_at: string;
  expires_at: string;
  is_expired: boolean;
};

export async function getSetupSession(code: string) {
  const { data } = await api.get<SetupSessionInfo>(`/agents/setup-sessions/${code}`);
  return data;
}

export async function confirmSetupSession(code: string) {
  const { data } = await api.post<{ ok: boolean; agent_id: string; hostname: string }>(
    `/agents/setup-sessions/${code}/confirm`,
  );
  return data;
}

export type InstallerInfo = {
  download_url: string;
  version: string | null;
  filename: string;
  /** Office server: the download is KraetdNet-Setup.exe with the office settings built in (needs sign-in). */
  package?: boolean;
  /** Office server: whether this person may download it (owners and admins). */
  can_download?: boolean;
};

/**
 * Office server: downloads KraetdNet-Setup.exe for another PC. Asks for a
 * fresh short-lived signed link and lets the browser download it itself (its own progress bar,
 * nothing held in memory).
 */
export async function downloadSetupPackage() {
  const info = await getInstallerInfo();
  if (!info.package || !info.can_download) {
    throw new Error("Only the office owner or an admin can download the setup program.");
  }
  window.location.assign(info.download_url);
}

export async function getInstallerInfo(): Promise<InstallerInfo> {
  const { data } = await api.get<InstallerInfo>("/agents/installer");
  return data;
}

export type PendingSession = {
  // `kind` discriminates between the two enrollment flows:
  //   - "setup_session": .exe-initiated → owner approves at /connect/<code>
  //   - "pc_enrollment": browser /join + office code → owner approves
  //     at /approve/<code> after typing the 4-digit OOB
  kind: "setup_session" | "pc_enrollment";
  code: string;
  hostname: string;
  created_at: string;
  expires_at: string;
};

export async function listPendingSessions(): Promise<PendingSession[]> {
  const { data } = await api.get<PendingSession[]>("/agents/pending-sessions");
  return data;
}

export async function uploadInstaller(file: File, version: string): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  form.append("version", version);
  await api.post("/agents/installer/upload", form, { timeout: 120_000 });
}

export type InstallerRelease = {
  id: number;
  version: string;
  filename: string;
  size_bytes: number;
  sha256: string;
  is_security_release: boolean;
  is_active: boolean;
  uploaded_at: string;
  uploaded_via: string;
  uploaded_from_ip: string | null;
  uploaded_by_email: string | null;
};

export async function listInstallerReleases(): Promise<InstallerRelease[]> {
  const { data } = await api.get<{ releases: InstallerRelease[] }>("/agents/installer/releases");
  return data.releases;
}

export async function deleteInstallerRelease(id: number): Promise<void> {
  await api.delete(`/agents/installer/releases/${id}`);
}

export async function activateInstallerRelease(id: number): Promise<void> {
  await api.post(`/agents/installer/releases/${id}/activate`);
}
